/**
 * FOUNDING-1 — the kit page's plain helpers (no components, so React
 * Refresh keeps the component files hot).
 */
import { useEffect, useState } from "react";
import { PLAN_DESCRIPTIONS, PLAN_LABELS, PLAN_PRICING } from "@/services/billing";
import {
  FOUNDING_LINE,
  FOUNDING_PASS,
  FOUNDING_TIME_ZONE,
  KIT_DEFAULTS,
  KIT_HOODIES,
  KIT_PATCHES,
  KIT_PINS,
  type HoodieSize,
  type KitDesign,
  type PassTier,
} from "@/config/founding";
import { FoundingKitError, PassCheckoutError, type KitPicks, type ShipTo } from "@/services/founding";

export type StepKey = "hoodie" | "pin" | "patch" | "ship" | "review";

/** The builder's picks while they're being made — any can be missing. */
export interface Draft {
  hoodie: string | null;
  size: HoodieSize | null;
  pin: string | null;
  patch: string | null;
}

/** A design by id — or, with nothing picked yet, the one the page leads with. */
function byId(list: KitDesign[], id: string | null, fallback: string): KitDesign {
  return list.find((d) => d.id === id) ?? list.find((d) => d.id === fallback) ?? list[0];
}
export const hoodieById = (id: string | null) => byId(KIT_HOODIES, id, KIT_DEFAULTS.hoodie);
export const pinById = (id: string | null) => byId(KIT_PINS, id, KIT_DEFAULTS.pin);
export const patchById = (id: string | null) => byId(KIT_PATCHES, id, KIT_DEFAULTS.patch);

/** "Navy · Label · H21" — a hoodie's full name, code included. */
export const designTitle = (d: KitDesign) => (d.code ? `${d.name} · ${d.code}` : d.name);

// ── Countries & address labels ─────────────────────────────────────────

let names: Intl.DisplayNames | null | undefined;
export function countryName(code: string): string {
  if (names === undefined) {
    try {
      names = new Intl.DisplayNames(["en"], { type: "region" });
    } catch {
      names = null;
    }
  }
  return (names && names.of(code)) || code;
}

const REGION_REQUIRED = new Set(["US", "CA", "AU", "MX", "BR", "IN", "CN", "JP"]);
export const regionRequired = (country: string) => REGION_REQUIRED.has(country);

export function regionLabel(country: string): string {
  if (country === "US" || country === "AU" || country === "MX" || country === "BR" || country === "IN") return "State";
  if (country === "CA") return "Province";
  if (country === "JP") return "Prefecture";
  return "State / region";
}

// Countries whose addresses have no postal code (Google's address data).
const NO_POSTAL = new Set(
  (
    "AE AG AO AW BF BI BJ BO BS BW BZ CD CF CG CI CK CM DJ DM ER FJ GA GD GH GM GQ GY HK " +
    "JM KI KM KN LY ML MO MR MW NR NU QA RW SB SC SL SR SS ST TD TG TK TL TO TV UG VU YE ZW"
  ).split(" "),
);
export const postalRequired = (country: string) => !NO_POSTAL.has(country);

export function postalLabel(country: string): string {
  if (country === "US") return "ZIP code";
  if (country === "GB") return "Postcode";
  return "Postal code";
}

// ── Validation ────────────────────────────────────────────────────────

export interface Problem {
  step: StepKey;
  /** The field to focus, when there is one. */
  field?: keyof ShipTo;
  message: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function problemsWith(draft: Draft, ship: ShipTo): Problem[] {
  const out: Problem[] = [];
  if (!draft.hoodie) out.push({ step: "hoodie", message: "Pick a hoodie" });
  if (!draft.size) out.push({ step: "hoodie", message: "Choose a size" });
  if (!draft.pin) out.push({ step: "pin", message: "Pick a pin" });
  if (!draft.patch) out.push({ step: "patch", message: "Pick a patch" });
  const need = (field: keyof ShipTo, message: string) => {
    if (!ship[field].trim()) out.push({ step: "ship", field, message });
  };
  need("name", "Add the name on the package");
  need("line1", "Add a street address");
  need("city", "Add a city");
  if (regionRequired(ship.country)) need("region", `Add a ${regionLabel(ship.country).toLowerCase()}`);
  if (postalRequired(ship.country)) need("postal", `Add a ${postalLabel(ship.country)}`);
  if (!ship.email.trim()) out.push({ step: "ship", field: "email", message: "Add an email for tracking" });
  else if (!EMAIL.test(ship.email.trim()))
    out.push({ step: "ship", field: "email", message: "That email doesn't look right" });
  return out;
}

export function completePicks(draft: Draft): KitPicks | null {
  return draft.hoodie && draft.size && draft.pin && draft.patch
    ? { hoodie: draft.hoodie, size: draft.size, pin: draft.pin, patch: draft.patch }
    : null;
}

/** What to tell the member when sending their picks fails. */
export function sendError(err: unknown, picksCloseAt: string): string {
  const code = err instanceof FoundingKitError ? err.code : "unavailable";
  if (code === "picks_closed") return `Picks closed on ${shortDay(picksCloseAt)}, so this kit can't change now.`;
  if (code === "invalid_token") return `This link isn't working anymore. Write to ${FOUNDING_LINE} and we'll sort it out.`;
  if (code === "invalid_claim") return "Part of the address didn't go through. Check it over and try again.";
  if (code === "pass_closed") return `The Founding Pass closed on ${shortDay(picksCloseAt)}.`;
  if (code === "sold_out") return "All the Founding Passes are spoken for.";
  return "That didn't go through. Check your connection and try again.";
}

/** Why "Get the Founding Pass" stopped, in words for the member. */
export function passError(err: unknown, closesAt: string): string {
  if (err instanceof PassCheckoutError) {
    switch (err.code) {
      case "pass_closed":
        return `The Founding Pass closed on ${shortDay(closesAt)}.`;
      case "sold_out":
        return "All the Founding Passes are spoken for.";
      case "org_has_pass":
        return `This Orbit account already has a Founding Pass${err.passNumber ? ` (No. ${String(err.passNumber).padStart(2, "0")})` : ""}.`;
      case "invalid_token":
        return `This link isn't working anymore. Write to ${FOUNDING_LINE} and we'll sort it out.`;
      case "unavailable":
        return "Checkout didn't open. Check your connection and try again.";
      default:
        return err.message;
    }
  }
  return sendError(err, closesAt);
}

// ── Formatting ────────────────────────────────────────────────────────

export function money(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
}

/** "Oct 31", on Orbit's clock (Pacific) — a deadline reads the same everywhere. */
export function shortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: FOUNDING_TIME_ZONE });
  } catch {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
}

/** "SEP 2026" — the Pass ticket's dates. */
export function monthYear(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" }).toUpperCase();
}

/** "September 2029" — for sentences (monthYear is the ticket's caps). */
export function monthYearLong(d: Date): string {
  try {
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: FOUNDING_TIME_ZONE });
  } catch {
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }
}

/** The address as lines, for the review, the label and the confirmation. */
export function addressLines(s: ShipTo): string[] {
  const street = [s.line1.trim(), s.line2.trim()].filter(Boolean).join(", ");
  const place = [s.city.trim(), [s.region.trim(), s.postal.trim()].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return [s.name.trim(), street, place, s.country ? countryName(s.country) : ""].filter(Boolean);
}

// ── Scroll tracking ───────────────────────────────────────────────────

/**
 * Which step's section is crossing the middle of the viewport — the
 * desktop stage shows that step's piece. Sections carry
 * `data-kit-step="<StepKey>"`.
 */
export function useActiveStep(root: HTMLElement | null, fallback: StepKey = "hoodie"): StepKey {
  const [active, setActive] = useState<StepKey>(fallback);
  useEffect(() => {
    if (!root || typeof IntersectionObserver === "undefined") return;
    const sections = Array.from(root.querySelectorAll<HTMLElement>("[data-kit-step]"));
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(e.target.getAttribute("data-kit-step") as StepKey);
        }
      },
      { rootMargin: "-45% 0px -50% 0px" },
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [root]);
  return active;
}

// ── The Pass ────────────────────────────────────────────────────────────

/** A Pass's numbers, all derived — never typed twice. */
export function passNumbers(tier: PassTier) {
  const P = FOUNDING_PASS;
  const t = P.tiers[tier];
  const plan = PLAN_LABELS[t.plan];
  const regular = PLAN_PRICING[t.plan];
  const yearly = regular.yearly ?? regular.monthly * 12;
  return {
    tier,
    plan,
    /** What the plan is, in the app's own words. */
    description: PLAN_DESCRIPTIONS[t.plan],
    /** Paid once, for `years`. */
    price: t.price,
    yearly,
    threeYears: yearly * P.years,
    saves: Math.round((yearly * P.years - t.price) * 100) / 100,
    perMonth: t.price / (P.years * 12),
    renewal: Math.round(yearly * (100 - P.afterPct)) / 100,
  };
}

export const PRIMARY_BUTTON =
  "inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[hsl(var(--orbit-blue-fill))] px-7 text-[16px] font-semibold text-white transition-[filter,opacity] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:opacity-70";
export const QUIET_BUTTON =
  "inline-flex h-12 items-center justify-center rounded-full px-6 text-[15px] font-semibold text-white/70 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70";
